import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Autocomplete,
  Container,
  Typography,
  Box,
  Paper,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tooltip,
  Checkbox,
  FormControlLabel,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Collapse,
  Card,
  CardContent,
  CardActions,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Business as BusinessIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Settings as SettingsIcon,
  AutoAwesome as AutoAwesomeIcon,
  Language as LanguageIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { 
  companyAPI, 
  referenceAPI,
  industryAPI,
  Company, 
  Country, 
  IndustryLevel01,
  IndustryLevel02,
  IndustryLevel03,
  IndustryLevel04,
  CompanySite,
  CompanyUser,
  User,
  CompanyEnrichmentResponse,
  IndustrySearchResult,
} from '../services/api';
import { buildIndustrySearchQueriesFromEnrichment, scoreIndustryResultFromEnrichment } from '../utils/industrySuggestion';
import { useAuth } from '../contexts/AuthContext';
import { useCompany } from '../contexts/CompanyContext';
import { useBilling } from '../contexts/BillingContext';
import ReactMarkdown from 'react-markdown';
import { ClientBillingTab } from '../components/Billing';

// Tab panel component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const CompanySettings: React.FC = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { userPermissions, refreshCompanies } = useCompany();
  const { billingStatus } = useBilling();
  
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [hasEditPermission, setHasEditPermission] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  
  // Reference data
  const [countries, setCountries] = useState<Country[]>([]);
  const [level01Industries, setLevel01Industries] = useState<IndustryLevel01[]>([]);
  const [level02Industries, setLevel02Industries] = useState<IndustryLevel02[]>([]);
  const [level03Industries, setLevel03Industries] = useState<IndustryLevel03[]>([]);
  const [level04Industries, setLevel04Industries] = useState<IndustryLevel04[]>([]);
  
  // Sites and Users
  const [sites, setSites] = useState<CompanySite[]>([]);
  const [users, setUsers] = useState<CompanyUser[]>([]);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  
  // Dialog states
  const [siteDialogOpen, setSiteDialogOpen] = useState(false);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [editingSite, setEditingSite] = useState<CompanySite | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{ type: 'site' | 'user'; id: number; name: string } | null>(null);
  
  // Site form
  const [siteForm, setSiteForm] = useState({
    name: '',
    engineers: false,
    manufacturers_internal: false,
    manufacturers_outsourced: false,
    purchases: false,
    holds_inventory: false,
    raw_material_part_count: 0,
    finished_good_part_count: 0,
  });
  
  // User assignment form
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [isPrimaryUser, setIsPrimaryUser] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    name: '',
    postcode: '',
    city: '',
    country: '',
    employee_count: 0,
    industry_level01: '',
    industry_level02: '',
    industry_level03: '',
    industry_level04: '',
    website_url: '',
  });

  // Industry search (Level 4 search with full hierarchy returned)
  const [industrySearchQuery, setIndustrySearchQuery] = useState<string>('');
  const [industrySearchValue, setIndustrySearchValue] = useState<IndustrySearchResult | null>(null);
  const [industrySearchOptions, setIndustrySearchOptions] = useState<IndustrySearchResult[]>([]);
  const [industrySearchLoading, setIndustrySearchLoading] = useState<boolean>(false);
  const [industryAutoSelectedMessage, setIndustryAutoSelectedMessage] = useState<string | null>(null);
  
  // Company enrichment state
  const [enrichmentLoading, setEnrichmentLoading] = useState(false);
  const [enrichmentResult, setEnrichmentResult] = useState<CompanyEnrichmentResponse | null>(null);
  const [enrichmentError, setEnrichmentError] = useState<string | null>(null);
  const [showFullProfile, setShowFullProfile] = useState(false);

  useEffect(() => {
    if (companyId) {
      loadData();
    }
  }, [companyId]);

  const loadData = async () => {
    if (!companyId) return;
    
    try {
      setLoading(true);
      setAccessDenied(false);
      console.log('✅ Loading company settings for ID:', companyId);
      
      const [companyData, countriesData, level01Data, sitesData, usersData] = await Promise.all([
        companyAPI.getCompany(parseInt(companyId)),
        referenceAPI.getCountries(),
        industryAPI.getLevel01Industries(),
        companyAPI.getCompanySites(parseInt(companyId)),
        companyAPI.getCompanyUsers(parseInt(companyId)),
      ]);
      
      // Check if user has edit permission (primary user, consultant admin, or Django admin)
      const currentUserInCompany = usersData.find(
        (u: CompanyUser) => u.user_email === user?.email
      );
      const isPrimaryUser = currentUserInCompany?.is_primary_user || false;
      const isConsultantAdmin = userPermissions?.can_manage_clients || false;
      const isSuperAdmin = userPermissions?.is_super_admin || false;
      
      const canEdit = isPrimaryUser || isConsultantAdmin || isSuperAdmin;
      setHasEditPermission(canEdit);
      
      // If user doesn't have any access to this company, deny access
      if (!currentUserInCompany && !isConsultantAdmin && !isSuperAdmin) {
        console.log('❌ Access denied - user not assigned to company and not an admin');
        setAccessDenied(true);
        setLoading(false);
        return;
      }
      
      console.log('🔐 Permission check:', { isPrimaryUser, isConsultantAdmin, isSuperAdmin, canEdit });
      
      setCompany(companyData);
      setCountries(countriesData);
      setLevel01Industries(level01Data);
      setSites(sitesData);
      setUsers(usersData);
      
      // Set form data from company
      setFormData({
        name: companyData.name || '',
        postcode: companyData.postcode || '',
        city: companyData.city || '',
        country: companyData.country?.toString() || '',
        employee_count: companyData.employee_count || 0,
        industry_level01: '',
        industry_level02: '',
        industry_level03: '',
        industry_level04: companyData.industry_level04?.toString() || '',
        website_url: companyData.website_url || '',
      });
      
      // Load saved company research if available
      if (companyData.company_research) {
        console.log('✅ Loading saved company research');
        setEnrichmentResult(companyData.company_research);
      }
      
      console.log('✅ Company settings loaded:', companyData.name);
      console.log('✅ Sites loaded:', sitesData.length);
      console.log('✅ Users loaded:', usersData.length);
      
      // Load the complete industry hierarchy if company has an existing classification
      if (companyData.industry_level04) {
        console.log('🔍 Found existing industry classification, loading hierarchy...');
        await loadIndustryHierarchyFromLevel04(companyData.industry_level04);
      }
      
    } catch (error: any) {
      console.error('Failed to load company data:', error);
      // Check if it's an access denied error from the backend
      if (error?.response?.status === 403) {
        setAccessDenied(true);
      } else {
        setError('Failed to load company settings');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadAllUsers = async () => {
    try {
      const usersData = await companyAPI.getAllUsers();
      setAllUsers(usersData);
    } catch (error) {
      console.error('Failed to load all users:', error);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
    setSuccess('');
    
    // Handle cascading industry dropdowns
    if (field === 'industry_level01') {
      setFormData(prev => ({ 
        ...prev, 
        [field]: value,
        industry_level02: '',
        industry_level03: '',
        industry_level04: ''
      }));
      if (value) {
        loadLevel02Industries(parseInt(value));
      } else {
        setLevel02Industries([]);
        setLevel03Industries([]);
        setLevel04Industries([]);
      }
    } else if (field === 'industry_level02') {
      setFormData(prev => ({ 
        ...prev, 
        [field]: value,
        industry_level03: '',
        industry_level04: ''
      }));
      if (value) {
        loadLevel03Industries(parseInt(value));
      } else {
        setLevel03Industries([]);
        setLevel04Industries([]);
      }
    } else if (field === 'industry_level03') {
      setFormData(prev => ({ 
        ...prev, 
        [field]: value,
        industry_level04: ''
      }));
      if (value) {
        loadLevel04Industries(parseInt(value));
      } else {
        setLevel04Industries([]);
      }
    }
  };

  const loadLevel02Industries = async (level01Id: number) => {
    try {
      const level02Data = await industryAPI.getLevel02Industries(level01Id);
      setLevel02Industries(level02Data);
    } catch (error) {
      console.error('Failed to load level 02 industries:', error);
    }
  };

  const loadLevel03Industries = async (level02Id: number) => {
    try {
      const level03Data = await industryAPI.getLevel03Industries(level02Id);
      setLevel03Industries(level03Data);
    } catch (error) {
      console.error('Failed to load level 03 industries:', error);
    }
  };

  const loadLevel04Industries = async (level03Id: number) => {
    try {
      const level04Data = await industryAPI.getLevel04Industries(level03Id);
      setLevel04Industries(level04Data);
    } catch (error) {
      console.error('Failed to load level 04 industries:', error);
    }
  };

  const loadIndustryHierarchyFromLevel04 = async (level04Id: number) => {
    try {
      const [allLevel01, allLevel02, allLevel03, allLevel04] = await Promise.all([
        industryAPI.getLevel01Industries(),
        industryAPI.getLevel02Industries(),
        industryAPI.getLevel03Industries(),
        industryAPI.getLevel04Industries(),
      ]);
      
      const targetLevel04 = allLevel04.find(industry => industry.id === level04Id);
      if (!targetLevel04) return;
      
      const targetLevel03 = allLevel03.find(industry => industry.id === targetLevel04.level03);
      if (!targetLevel03) return;
      
      const targetLevel02 = allLevel02.find(industry => industry.id === targetLevel03.level02);
      if (!targetLevel02) return;
      
      const targetLevel01 = allLevel01.find(industry => industry.id === targetLevel02.level01);
      if (!targetLevel01) return;
      
      setLevel01Industries(allLevel01);
      setLevel02Industries(allLevel02.filter(i => i.level01 === targetLevel01.id));
      setLevel03Industries(allLevel03.filter(i => i.level02 === targetLevel02.id));
      setLevel04Industries(allLevel04.filter(i => i.level03 === targetLevel03.id));
      
      setFormData(prev => ({
        ...prev,
        industry_level01: targetLevel01.id.toString(),
        industry_level02: targetLevel02.id.toString(),
        industry_level03: targetLevel03.id.toString(),
        industry_level04: targetLevel04.id.toString(),
      }));

      // Sync the search field too (helps users understand what was selected)
      setIndustrySearchValue({
        level01: { id: targetLevel01.id, code: targetLevel01.code, name: targetLevel01.name },
        level02: { id: targetLevel02.id, code: targetLevel02.code, name: targetLevel02.name },
        level03: { id: targetLevel03.id, code: targetLevel03.code, name: targetLevel03.name },
        level04: { id: targetLevel04.id, code: targetLevel04.code, name: targetLevel04.name },
        display: `${targetLevel01.code} ${targetLevel01.name} > ${targetLevel02.code} ${targetLevel02.name} > ${targetLevel03.code} ${targetLevel03.name} > ${targetLevel04.code} ${targetLevel04.name}`,
      });
      
    } catch (error) {
      console.error('Failed to load industry hierarchy:', error);
    }
  };

  const applyIndustrySearchSelection = async (selection: IndustrySearchResult) => {
    try {
      setFormData(prev => ({
        ...prev,
        industry_level01: selection.level01.id.toString(),
        industry_level02: selection.level02.id.toString(),
        industry_level03: selection.level03.id.toString(),
        industry_level04: selection.level04.id.toString(),
      }));

      const [level02Data, level03Data, level04Data] = await Promise.all([
        industryAPI.getLevel02Industries(selection.level01.id),
        industryAPI.getLevel03Industries(selection.level02.id),
        industryAPI.getLevel04Industries(selection.level03.id),
      ]);

      setLevel02Industries(level02Data);
      setLevel03Industries(level03Data);
      setLevel04Industries(level04Data);
    } catch (error) {
      console.error('Failed to apply industry search selection:', error);
    }
  };

  const autoSelectIndustryFromEnrichment = async (result: CompanyEnrichmentResponse) => {
    try {
      // Don't override a selection the user already made.
      if (formData.industry_level04) return;

      const queries = buildIndustrySearchQueriesFromEnrichment(result);
      if (queries.length === 0) return;

      let best: IndustrySearchResult | null = null;
      let bestScore = -1;

      for (const q of queries.slice(0, 5)) {
        const hits = await industryAPI.searchIndustries(q, 20);
        for (const hit of hits) {
          const score = scoreIndustryResultFromEnrichment(hit, result);
          if (score > bestScore) {
            bestScore = score;
            best = hit;
          }
        }
      }

      // Avoid random/low-confidence picks.
      if (!best || bestScore < 2) return;

      setIndustrySearchValue(best);
      await applyIndustrySearchSelection(best);
      setIndustryAutoSelectedMessage('Industry was suggested from company research. Please review before saving.');
    } catch (error) {
      console.error('Failed to auto-select industry from enrichment:', error);
    }
  };

  // Debounced industry search
  useEffect(() => {
    const q = industrySearchQuery.trim();
    if (q.length < 2) {
      setIndustrySearchOptions([]);
      return;
    }

    const handle = window.setTimeout(async () => {
      setIndustrySearchLoading(true);
      try {
        const results = await industryAPI.searchIndustries(q, 50);
        setIndustrySearchOptions(results);
      } catch (error) {
        console.error('Error searching industries:', error);
        setIndustrySearchOptions([]);
      } finally {
        setIndustrySearchLoading(false);
      }
    }, 250);

    return () => window.clearTimeout(handle);
  }, [industrySearchQuery]);

  const handleSave = async () => {
    if (!companyId || !company) return;
    
    try {
      setSaving(true);
      
      // Format website URL - add https:// if missing protocol
      let websiteUrl = formData.website_url?.trim() || undefined;
      if (websiteUrl && !websiteUrl.match(/^https?:\/\//i)) {
        websiteUrl = `https://${websiteUrl}`;
      }
      
      const updateData = {
        name: formData.name,
        city: formData.city,
        postcode: formData.postcode,
        country: parseInt(formData.country) || company.country,
        employee_count: formData.employee_count,
        industry_level04: parseInt(formData.industry_level04) || undefined,
        website_url: websiteUrl,
        kpi_tracking_frequency: company.kpi_tracking_frequency,
        kpi_display_periods: company.kpi_display_periods,
      };
      
      const updatedCompany = await companyAPI.updateCompany(parseInt(companyId), updateData);
      setCompany(updatedCompany);
      
      // Refresh the company list in context to update other pages
      await refreshCompanies();
      
      setSuccess('Company settings saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (error) {
      console.error('Failed to save company settings:', error);
      setError('Failed to save company settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Fetch company information using AI enrichment
   */
  const handleEnrichCompany = async () => {
    if (!formData.name.trim()) {
      setEnrichmentError('Company name is required');
      return;
    }
    
    setEnrichmentLoading(true);
    setEnrichmentError(null);
    setEnrichmentResult(null);
    setIndustryAutoSelectedMessage(null);
    
    try {
      // Pass company_id to save the research to the database
      const result = await companyAPI.enrichCompany(
        formData.name,
        formData.website_url || undefined,
        companyId ? parseInt(companyId) : undefined
      );
      
      setEnrichmentResult(result);
      setShowFullProfile(false); // Start collapsed
      
      // Auto-populate empty form fields from structured data
      if (result.structured_data) {
        const data = result.structured_data;
        
        // Auto-fill city if empty
        if (!formData.city && data.headquarters_city) {
          setFormData(prev => ({ ...prev, city: data.headquarters_city || '' }));
        }
        
        // Auto-fill country if we have headquarters_country
        if (data.headquarters_country) {
          const matchedCountry = countries.find(
            c => c.name.toLowerCase() === data.headquarters_country?.toLowerCase()
          );
          if (matchedCountry && !formData.country) {
            setFormData(prev => ({ ...prev, country: matchedCountry.id.toString() }));
          }
        }
        
        // Auto-fill employee count if empty
        if (!formData.employee_count && data.employee_count_estimate) {
          const estimate = data.employee_count_estimate.toString();
          const numMatch = estimate.match(/\d+/);
          if (numMatch) {
            setFormData(prev => ({ ...prev, employee_count: parseInt(numMatch[0]) }));
          }
        }
      }

      // Suggest industry selection from enrichment (only if not already selected)
      void autoSelectIndustryFromEnrichment(result);
      
    } catch (err) {
      console.error('Error enriching company:', err);
      setEnrichmentError('Failed to fetch company information. Please try again.');
    } finally {
      setEnrichmentLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(`/company/${companyId}`);
  };

  // Site management
  const handleAddSite = () => {
    setEditingSite(null);
    setSiteForm({
      name: '',
      engineers: false,
      manufacturers_internal: false,
      manufacturers_outsourced: false,
      purchases: false,
      holds_inventory: false,
      raw_material_part_count: 0,
      finished_good_part_count: 0,
    });
    setSiteDialogOpen(true);
  };

  const handleEditSite = (site: CompanySite) => {
    setEditingSite(site);
    setSiteForm({
      name: site.name,
      engineers: site.engineers || false,
      manufacturers_internal: site.manufacturers_internal || false,
      manufacturers_outsourced: site.manufacturers_outsourced || false,
      purchases: site.purchases || false,
      holds_inventory: site.holds_inventory || false,
      raw_material_part_count: site.raw_material_part_count || 0,
      finished_good_part_count: site.finished_good_part_count || 0,
    });
    setSiteDialogOpen(true);
  };

  const handleSaveSite = async () => {
    if (!companyId) return;
    
    try {
      if (editingSite) {
        await companyAPI.updateCompanySite(parseInt(companyId), editingSite.id, siteForm);
        setSuccess('Site updated successfully!');
      } else {
        await companyAPI.createCompanySite(parseInt(companyId), siteForm);
        setSuccess('Site added successfully!');
      }
      
      // Reload sites
      const sitesData = await companyAPI.getCompanySites(parseInt(companyId));
      setSites(sitesData);
      setSiteDialogOpen(false);
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (error) {
      console.error('Failed to save site:', error);
      setError('Failed to save site. Please try again.');
    }
  };

  const handleDeleteSite = (site: CompanySite) => {
    setItemToDelete({ type: 'site', id: site.id, name: site.name });
    setDeleteConfirmOpen(true);
  };

  // User management
  const handleAddUser = async () => {
    await loadAllUsers();
    setSelectedUserId(null);
    setIsPrimaryUser(false);
    setUserDialogOpen(true);
  };

  const handleAssignUser = async () => {
    if (!companyId || !selectedUserId) return;
    
    try {
      await companyAPI.assignUserToCompany(parseInt(companyId), selectedUserId, isPrimaryUser);
      setSuccess('User assigned successfully!');
      
      // Reload users
      const usersData = await companyAPI.getCompanyUsers(parseInt(companyId));
      setUsers(usersData);
      setUserDialogOpen(false);
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (error) {
      console.error('Failed to assign user:', error);
      setError('Failed to assign user. Please try again.');
    }
  };

  const handleRemoveUser = (user: CompanyUser) => {
    setItemToDelete({ type: 'user', id: user.id, name: user.user_email || user.user_name || 'User' });
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!companyId || !itemToDelete) return;
    
    try {
      if (itemToDelete.type === 'site') {
        await companyAPI.deleteCompanySite(parseInt(companyId), itemToDelete.id);
        const sitesData = await companyAPI.getCompanySites(parseInt(companyId));
        setSites(sitesData);
        setSuccess('Site deleted successfully!');
      } else {
        await companyAPI.removeUserFromCompany(parseInt(companyId), itemToDelete.id);
        const usersData = await companyAPI.getCompanyUsers(parseInt(companyId));
        setUsers(usersData);
        setSuccess('User removed successfully!');
      }
      
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (error) {
      console.error('Failed to delete:', error);
      setError(`Failed to delete ${itemToDelete.type}. Please try again.`);
    }
  };

  // Get users not already assigned to this company
  const getAvailableUsers = () => {
    const assignedUserIds = users.map(u => u.user_id);
    return allUsers.filter(u => !assignedUserIds.includes(u.id));
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading company settings...
        </Typography>
      </Container>
    );
  }

  if (accessDenied) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>Access Denied</Typography>
          <Typography variant="body1">
            You don't have permission to access settings for this company.
            Only primary users, consultant admins, or super admins can access company settings.
          </Typography>
        </Alert>
        <Button variant="contained" onClick={() => navigate(-1)}>
          Go Back
        </Button>
      </Container>
    );
  }

  if (!company) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">Company not found</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <BusinessIcon sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" component="h1">
              Company Settings
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {company.name}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<CancelIcon />}
            onClick={handleCancel}
            disabled={saving}
          >
            Cancel
          </Button>
          {hasEditPermission && (
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          )}
        </Box>
      </Box>

      {/* Permission Notice */}
      {!hasEditPermission && (
        <Alert severity="info" sx={{ mb: 3 }}>
          You have read-only access to this company's settings. Only primary users or admins can make changes.
        </Alert>
      )}

      {/* Success/Error Messages */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={tabValue} 
          onChange={(_, newValue) => setTabValue(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<SettingsIcon />} label="General" iconPosition="start" />
          <Tab icon={<LocationIcon />} label={`Sites (${sites.length})`} iconPosition="start" />
          <Tab icon={<PersonIcon />} label={`Users (${users.length})`} iconPosition="start" />
          <Tab icon={<AutoAwesomeIcon />} label="KPI Settings" iconPosition="start" />
          {/* Show billing tab for:
              1. Consultants managing client companies (can_manage_clients or is_super_admin)
              2. Client users whose company is set to "Client Pays" (so they can purchase credits) */}
          {(userPermissions?.can_manage_clients || 
            userPermissions?.is_super_admin || 
            (billingStatus?.type === 'client' && billingStatus?.billing_responsibility === 'client')) && (
            <Tab icon={<BusinessIcon />} label="Billing" iconPosition="start" />
          )}
        </Tabs>

        {/* General Settings Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Company Information
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Row 1 */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Company Name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  fullWidth
                  required
                  disabled={!hasEditPermission}
                />
                <TextField
                  label="City"
                  value={formData.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  fullWidth
                  required
                  disabled={!hasEditPermission}
                />
              </Box>
              
              {/* Row 2 */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Postcode"
                  value={formData.postcode}
                  onChange={(e) => handleInputChange('postcode', e.target.value)}
                  fullWidth
                  disabled={!hasEditPermission}
                />
                <FormControl fullWidth disabled={!hasEditPermission}>
                  <InputLabel>Country</InputLabel>
                  <Select
                    value={formData.country}
                    onChange={(e) => handleInputChange('country', e.target.value)}
                    label="Country"
                  >
                    {countries.map((country) => (
                      <MenuItem key={country.id} value={country.id.toString()}>
                        {country.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              
              {/* Row 3 - Employee Count and Sites Count (read-only) */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Employee Count"
                  type="number"
                  value={formData.employee_count}
                  onChange={(e) => handleInputChange('employee_count', parseInt(e.target.value) || 0)}
                  fullWidth
                  disabled={!hasEditPermission}
                />
                <TextField
                  label="Sites Count"
                  value={sites.length}
                  fullWidth
                  disabled
                  helperText="Manage sites in the Sites tab"
                />
              </Box>
              
              {/* Industry Classification */}
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Industry Classification
              </Typography>

              <Autocomplete
                fullWidth
                options={industrySearchOptions}
                value={industrySearchValue}
                inputValue={industrySearchQuery}
                onInputChange={(_, newValue, reason) => {
                  // Keep the user's original search text after selecting an option.
                  // Only update the input when the user is actively typing, or explicitly clearing.
                  if (reason === 'clear') {
                    setIndustrySearchQuery('');
                    return;
                  }
                  if (reason !== 'input') return;
                  setIndustrySearchQuery(newValue);
                }}
                onChange={(_, newValue) => {
                  setIndustrySearchValue(newValue);
                  if (newValue) {
                    void applyIndustrySearchSelection(newValue);
                  }
                }}
                getOptionLabel={(option) => option.display}
                filterOptions={(x) => x} // server-side filtering
                loading={industrySearchLoading}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="body2">{option.display}</Typography>
                      {option.level04.detail && (
                        <Typography variant="caption" color="text.secondary">
                          {option.level04.detail}
                        </Typography>
                      )}
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Industry search"
                    placeholder="Type a keyword or code (e.g. cosmetic, detergent, 1851)"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {industrySearchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              
              {formData.industry_level04 && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  ✅ Industry classification loaded: {
                    level04Industries.find(i => i.id.toString() === formData.industry_level04)?.name || 
                    `Level 4 ID ${formData.industry_level04}`
                  }
                </Alert>
              )}
              
              {/* Level 01 */}
              <FormControl fullWidth disabled={!hasEditPermission}>
                <InputLabel>Primary Industry (Level 1)</InputLabel>
                <Select
                  value={formData.industry_level01}
                  onChange={(e) => handleInputChange('industry_level01', e.target.value)}
                  label="Primary Industry (Level 1)"
                >
                  <MenuItem value=""><em>Select primary industry</em></MenuItem>
                  {level01Industries.map((industry) => (
                    <MenuItem key={industry.id} value={industry.id.toString()}>
                      {industry.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              {/* Level 02 */}
              {formData.industry_level01 && (
                <FormControl fullWidth disabled={!hasEditPermission}>
                  <InputLabel>Sector (Level 2)</InputLabel>
                  <Select
                    value={formData.industry_level02}
                    onChange={(e) => handleInputChange('industry_level02', e.target.value)}
                    label="Sector (Level 2)"
                  >
                    <MenuItem value=""><em>Select sector</em></MenuItem>
                    {level02Industries.map((industry) => (
                      <MenuItem key={industry.id} value={industry.id.toString()}>
                        {industry.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              
              {/* Level 03 */}
              {formData.industry_level02 && (
                <FormControl fullWidth disabled={!hasEditPermission}>
                  <InputLabel>Sub-Sector (Level 3)</InputLabel>
                  <Select
                    value={formData.industry_level03}
                    onChange={(e) => handleInputChange('industry_level03', e.target.value)}
                    label="Sub-Sector (Level 3)"
                  >
                    <MenuItem value=""><em>Select sub-sector</em></MenuItem>
                    {level03Industries.map((industry) => (
                      <MenuItem key={industry.id} value={industry.id.toString()}>
                        {industry.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              
              {/* Level 04 */}
              {formData.industry_level03 && (
                <FormControl fullWidth disabled={!hasEditPermission}>
                  <InputLabel>Specific Industry (Level 4)</InputLabel>
                  <Select
                    value={formData.industry_level04}
                    onChange={(e) => handleInputChange('industry_level04', e.target.value)}
                    label="Specific Industry (Level 4)"
                  >
                    <MenuItem value=""><em>Select specific industry</em></MenuItem>
                    {level04Industries.map((industry) => (
                      <MenuItem key={industry.id} value={industry.id.toString()}>
                        {industry.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {/* Level 4 description (Detail) */}
              {formData.industry_level04 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {level04Industries.find(i => i.id.toString() === formData.industry_level04)?.detail || ''}
                </Typography>
              )}
              
              {/* Company Research Section */}
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AutoAwesomeIcon color="secondary" />
                Company Research
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Use AI to fetch detailed company information from public sources. Enter the company website (optional) and click "Fetch Company Info".
              </Typography>
              
              {/* Website URL and Fetch Button */}
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 2 }}>
                <TextField
                  fullWidth
                  label="Company Website"
                  placeholder="https://www.example.com"
                  value={formData.website_url}
                  onChange={(e) => handleInputChange('website_url', e.target.value)}
                  disabled={!hasEditPermission}
                  InputProps={{
                    startAdornment: <LanguageIcon sx={{ mr: 1, color: 'action.active' }} />,
                  }}
                  helperText="Optional - helps AI provide more accurate information"
                />
                <Tooltip title="Use AI to research company information from public sources">
                  <span>
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={handleEnrichCompany}
                      disabled={enrichmentLoading || !formData.name.trim() || !hasEditPermission}
                      sx={{ 
                        minWidth: 180, 
                        height: 56,
                        whiteSpace: 'nowrap'
                      }}
                      startIcon={enrichmentLoading ? <CircularProgress size={20} color="inherit" /> : <AutoAwesomeIcon />}
                    >
                      {enrichmentLoading ? 'Researching...' : 'Fetch Company Info'}
                    </Button>
                  </span>
                </Tooltip>
              </Box>
              
              {/* Enrichment Error */}
              {enrichmentError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setEnrichmentError(null)}>
                  {enrichmentError}
                </Alert>
              )}

              {/* Industry auto-selection notice */}
              {industryAutoSelectedMessage && (
                <Alert severity="info" sx={{ mb: 2 }} onClose={() => setIndustryAutoSelectedMessage(null)}>
                  {industryAutoSelectedMessage}
                </Alert>
              )}
              
              {/* Enrichment Results */}
              {enrichmentResult && (
                <Card variant="outlined" sx={{ mb: 2, bgcolor: 'background.default' }}>
                  <CardContent>
                    {/* Summary Section */}
                    {enrichmentResult.structured_data && (
                      <Box sx={{ mb: 2 }}>
                        <Alert severity="success" sx={{ mb: 2 }}>
                          <Typography variant="subtitle2">
                            ✨ Company information fetched successfully!
                          </Typography>
                        </Alert>
                        
                        {enrichmentResult.structured_data.company_description && (
                          <Typography variant="body1" sx={{ mb: 2 }}>
                            {enrichmentResult.structured_data.company_description}
                          </Typography>
                        )}
                        
                        {/* Key Facts */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                          {enrichmentResult.structured_data.headquarters_city && (
                            <Chip 
                              icon={<LocationIcon />} 
                              label={`HQ: ${enrichmentResult.structured_data.headquarters_city}${enrichmentResult.structured_data.headquarters_country ? `, ${enrichmentResult.structured_data.headquarters_country}` : ''}`}
                              variant="outlined"
                            />
                          )}
                          {enrichmentResult.structured_data.employee_count_estimate && (
                            <Chip 
                              icon={<PersonIcon />} 
                              label={`~${enrichmentResult.structured_data.employee_count_estimate} employees`}
                              variant="outlined"
                            />
                          )}
                          {enrichmentResult.structured_data.founded_year && (
                            <Chip 
                              label={`Founded: ${enrichmentResult.structured_data.founded_year}`}
                              variant="outlined"
                            />
                          )}
                          {enrichmentResult.structured_data.is_manufacturer !== null && (
                            <Chip 
                              label={enrichmentResult.structured_data.is_manufacturer ? 'Manufacturer' : 'Non-Manufacturer'}
                              color={enrichmentResult.structured_data.is_manufacturer ? 'primary' : 'default'}
                              variant="outlined"
                            />
                          )}
                          {enrichmentResult.structured_data.holds_inventory !== null && (
                            <Chip 
                              label={enrichmentResult.structured_data.holds_inventory ? 'Holds Inventory' : 'No Inventory'}
                              color={enrichmentResult.structured_data.holds_inventory ? 'primary' : 'default'}
                              variant="outlined"
                            />
                          )}
                        </Box>
                        
                        {/* Industry Keywords */}
                        {enrichmentResult.structured_data.industry_keywords && enrichmentResult.structured_data.industry_keywords.length > 0 && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                              Industry Keywords:
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {enrichmentResult.structured_data.industry_keywords.map((keyword, idx) => (
                                <Chip key={idx} label={keyword} size="small" />
                              ))}
                            </Box>
                          </Box>
                        )}
                        
                        {enrichmentResult.structured_data.confidence_level && (
                          <Typography variant="caption" color="text.secondary">
                            Confidence: {enrichmentResult.structured_data.confidence_level}
                          </Typography>
                        )}
                      </Box>
                    )}
                    
                    {/* Full Profile Toggle */}
                    {enrichmentResult.full_profile && (
                      <Box>
                        <Button
                          onClick={() => setShowFullProfile(!showFullProfile)}
                          endIcon={showFullProfile ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          sx={{ mb: 1 }}
                        >
                          {showFullProfile ? 'Hide' : 'Show'} Full Research Report
                        </Button>
                        <Collapse in={showFullProfile}>
                          <Paper 
                            sx={{ 
                              p: 3, 
                              bgcolor: 'background.paper', 
                              maxHeight: 600, 
                              overflow: 'auto',
                              border: '1px solid',
                              borderColor: 'divider',
                              color: 'text.primary',
                              '& h1': { fontSize: '1.5rem', fontWeight: 600, mt: 3, mb: 1.5, color: 'primary.main' },
                              '& h2': { fontSize: '1.25rem', fontWeight: 600, mt: 2.5, mb: 1, color: 'primary.main' },
                              '& h3': { fontSize: '1.1rem', fontWeight: 600, mt: 2, mb: 1, color: 'text.primary' },
                              '& h4, & h5, & h6': { fontSize: '1rem', fontWeight: 600, mt: 1.5, mb: 0.5, color: 'text.primary' },
                              '& p': { mb: 1.5, lineHeight: 1.7, color: 'text.primary' },
                              '& ul, & ol': { pl: 3, mb: 1.5, color: 'text.primary' },
                              '& li': { mb: 0.5, color: 'text.primary' },
                              '& strong': { fontWeight: 600, color: 'text.primary' },
                              '& em': { fontStyle: 'italic', color: 'text.primary' },
                              '& hr': { my: 2, borderColor: 'divider' },
                              '& blockquote': { 
                                borderLeft: 3, 
                                borderColor: 'primary.main', 
                                pl: 2, 
                                py: 0.5,
                                my: 1.5,
                                bgcolor: 'action.hover',
                                fontStyle: 'italic',
                                color: 'text.primary'
                              },
                              '& code': {
                                bgcolor: 'action.hover',
                                px: 0.5,
                                py: 0.25,
                                borderRadius: 0.5,
                                fontFamily: 'monospace',
                                fontSize: '0.875em',
                                color: 'text.primary'
                              },
                              '& pre': {
                                bgcolor: 'action.hover',
                                p: 1.5,
                                borderRadius: 1,
                                overflow: 'auto',
                                border: '1px solid',
                                borderColor: 'divider',
                                '& code': {
                                  bgcolor: 'transparent',
                                  p: 0
                                }
                              },
                              '& a': { color: 'primary.main', textDecoration: 'underline' },
                              '& table': { 
                                borderCollapse: 'collapse', 
                                width: '100%', 
                                my: 2,
                                '& th, & td': {
                                  border: 1,
                                  borderColor: 'divider',
                                  p: 1,
                                  textAlign: 'left',
                                  color: 'text.primary'
                                },
                                '& th': {
                                  bgcolor: 'action.hover',
                                  fontWeight: 600
                                }
                              }
                            }}
                          >
                            <ReactMarkdown>
                              {enrichmentResult.full_profile}
                            </ReactMarkdown>
                          </Paper>
                        </Collapse>
                      </Box>
                    )}
                  </CardContent>
                  <CardActions>
                    <Button 
                      size="small" 
                      onClick={() => setEnrichmentResult(null)}
                    >
                      Clear Results
                    </Button>
                  </CardActions>
                </Card>
              )}
            </Box>
          </Box>
        </TabPanel>

        {/* Sites Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">
                Company Sites
              </Typography>
              {hasEditPermission && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddSite}
                >
                  Add Site
                </Button>
              )}
            </Box>
            
            {sites.length === 0 ? (
              <Alert severity="info">
                No sites configured. Add a site to get started.
              </Alert>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Site Name</TableCell>
                      <TableCell>Attributes</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sites.map((site) => (
                      <TableRow key={site.id}>
                        <TableCell>
                          <Typography variant="subtitle2">{site.name}</Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {site.engineers && <Chip label="Engineers" size="small" />}
                            {site.manufacturers_internal && <Chip label="Mfg Internal" size="small" />}
                            {site.manufacturers_outsourced && <Chip label="Mfg Outsourced" size="small" />}
                            {site.purchases && <Chip label="Purchases" size="small" />}
                            {site.holds_inventory && <Chip label="Inventory" size="small" />}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          {hasEditPermission && (
                            <>
                              <Tooltip title="Edit Site">
                                <IconButton onClick={() => handleEditSite(site)} size="small">
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete Site">
                                <IconButton 
                                  onClick={() => handleDeleteSite(site)} 
                                  size="small" 
                                  color="error"
                                  disabled={site.name === 'Company Level' && sites.length === 1}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </TabPanel>

        {/* Users Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">
                Company Users
              </Typography>
              {hasEditPermission && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddUser}
                >
                  Add User
                </Button>
              )}
            </Box>
            
            {users.length === 0 ? (
              <Alert severity="info">
                No users assigned to this company.
              </Alert>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle2">
                              {user.user_name || user.user_email}
                            </Typography>
                            {user.is_primary_user && (
                              <Chip label="Primary" size="small" color="primary" />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>{user.user_email}</TableCell>
                        <TableCell>
                          <Chip 
                            label={user.role?.replace('_', ' ').toUpperCase() || 'User'} 
                            size="small" 
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="right">
                          {hasEditPermission && (
                            <Tooltip title="Remove User">
                              <IconButton 
                                onClick={() => handleRemoveUser(user)} 
                                size="small" 
                                color="error"
                                disabled={user.is_primary_user}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </TabPanel>

        {/* Billing Tab - For consultants managing clients OR client users who pay for their own audits */}
        {/* KPI Settings Tab */}
        <TabPanel value={tabValue} index={3}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              KPI Tracking Settings
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Configure how KPIs are tracked and displayed for this company.
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 600 }}>
              <FormControl fullWidth>
                <InputLabel>Tracking Frequency</InputLabel>
                <Select
                  value={company?.kpi_tracking_frequency || 'monthly'}
                  label="Tracking Frequency"
                  disabled={!hasEditPermission}
                  onChange={(e) => {
                    if (company) {
                      setCompany({ ...company, kpi_tracking_frequency: e.target.value as any });
                    }
                  }}
                >
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                  <MenuItem value="quarterly">Quarterly</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Number of Periods to Display"
                type="number"
                value={company?.kpi_display_periods || 12}
                onChange={(e) => {
                  if (company) {
                    setCompany({ ...company, kpi_display_periods: parseInt(e.target.value) || 12 });
                  }
                }}
                disabled={!hasEditPermission}
                fullWidth
                helperText="How many time periods to show in the KPI grid (e.g., 12 months)"
                inputProps={{ min: 4, max: 24 }}
              />

              <Alert severity="info">
                <Typography variant="body2">
                  💡 The KPI dashboard will show the last {company?.kpi_display_periods || 12} complete periods ending with the previous {
                    company?.kpi_tracking_frequency === 'weekly' ? 'week' :
                    company?.kpi_tracking_frequency === 'quarterly' ? 'quarter' : 'month'
                  }.
                </Typography>
              </Alert>
            </Box>
          </Box>
        </TabPanel>

        {(userPermissions?.can_manage_clients || 
          userPermissions?.is_super_admin || 
          (billingStatus?.type === 'client' && billingStatus?.billing_responsibility === 'client')) && (
          <TabPanel value={tabValue} index={4}>
            <Box sx={{ p: 2 }}>
              <ClientBillingTab
                companyId={parseInt(companyId || '0')}
                companyName={company?.name || 'Company'}
                isConsultantView={userPermissions?.can_manage_clients || userPermissions?.is_super_admin || false}
              />
            </Box>
          </TabPanel>
        )}
      </Paper>

      {/* Site Dialog */}
      <Dialog open={siteDialogOpen} onClose={() => setSiteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingSite ? 'Edit Site' : 'Add New Site'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Site Name"
              value={siteForm.name}
              onChange={(e) => setSiteForm({ ...siteForm, name: e.target.value })}
              fullWidth
              required
            />
            
            <Typography variant="subtitle2" sx={{ mt: 2 }}>
              Site Attributes
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={siteForm.engineers}
                    onChange={(e) => setSiteForm({ ...siteForm, engineers: e.target.checked })}
                  />
                }
                label="Engineers"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={siteForm.manufacturers_internal}
                    onChange={(e) => setSiteForm({ ...siteForm, manufacturers_internal: e.target.checked })}
                  />
                }
                label="Manufacturers (Internal)"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={siteForm.manufacturers_outsourced}
                    onChange={(e) => setSiteForm({ ...siteForm, manufacturers_outsourced: e.target.checked })}
                  />
                }
                label="Manufacturers (Outsourced)"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={siteForm.purchases}
                    onChange={(e) => setSiteForm({ ...siteForm, purchases: e.target.checked })}
                  />
                }
                label="Purchases"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={siteForm.holds_inventory}
                    onChange={(e) => setSiteForm({ ...siteForm, holds_inventory: e.target.checked })}
                  />
                }
                label="Holds Inventory"
              />
            </Box>
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Raw Material Part Count"
                type="number"
                value={siteForm.raw_material_part_count}
                onChange={(e) => setSiteForm({ ...siteForm, raw_material_part_count: parseInt(e.target.value) || 0 })}
                fullWidth
              />
              <TextField
                label="Finished Good Part Count"
                type="number"
                value={siteForm.finished_good_part_count}
                onChange={(e) => setSiteForm({ ...siteForm, finished_good_part_count: parseInt(e.target.value) || 0 })}
                fullWidth
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSiteDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleSaveSite} 
            variant="contained"
            disabled={!siteForm.name.trim()}
          >
            {editingSite ? 'Update' : 'Add'} Site
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Assignment Dialog */}
      <Dialog open={userDialogOpen} onClose={() => setUserDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign User to Company</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Select User</InputLabel>
              <Select
                value={selectedUserId || ''}
                onChange={(e) => setSelectedUserId(e.target.value as number)}
                label="Select User"
              >
                {getAvailableUsers().map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.email} ({user.first_name} {user.last_name})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={isPrimaryUser}
                  onChange={(e) => setIsPrimaryUser(e.target.checked)}
                />
              }
              label="Set as Primary User"
            />
            
            {getAvailableUsers().length === 0 && (
              <Alert severity="info">
                All users are already assigned to this company.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleAssignUser} 
            variant="contained"
            disabled={!selectedUserId}
          >
            Assign User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to {itemToDelete?.type === 'user' ? 'remove' : 'delete'}{' '}
            <strong>{itemToDelete?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            {itemToDelete?.type === 'user' ? 'Remove' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CompanySettings;
